import { memo, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { GridPosition } from "@/hooks/useIconGridPosition";
import { WIDGET_GRID_SIZE, type WidgetSize } from "@/types/widget";

interface DesktopGridOverlayProps {
  isActive: boolean;
  gridDimensions: {
    rows: number;
    cols: number;
    gridCellWidth: number;
    gridCellHeight: number;
  };
  occupiedCells: Set<string>;
  hoveredPosition?: GridPosition | null;
  hoveredSize?: WidgetSize | "icon";
}

/**
 * Optimized DesktopGridOverlay
 * Uses CSS background patterns for the grid lines to avoid rendering hundreds of divs.
 * Only renders individual cells for the hovered region.
 */
function DesktopGridOverlay({
  isActive,
  gridDimensions,
  occupiedCells,
  hoveredPosition,
  hoveredSize = "icon",
}: DesktopGridOverlayProps) {
  const { rows, cols, gridCellWidth, gridCellHeight } = gridDimensions;

  // Icons are centered in cells. To match DesktopIcon.tsx exactly:
  const iconOffsetX = (gridCellWidth - 80) / 2;
  const iconOffsetY = (gridCellHeight - 100) / 2;

  // Calculate hovered cells
  const hoveredCells = useMemo(() => {
    if (!hoveredPosition) return [];

    const cells: string[] = [];

    const hRows =
      hoveredSize === "icon" ? 1 : WIDGET_GRID_SIZE[hoveredSize].rows;
    const hCols =
      hoveredSize === "icon" ? 1 : WIDGET_GRID_SIZE[hoveredSize].cols;

    for (let r = 0; r < hRows; r++) {
      for (let c = 0; c < hCols; c++) {
        cells.push(`${hoveredPosition.row + r}:${hoveredPosition.col + c}`);
      }
    }
    return cells;
  }, [hoveredPosition, hoveredSize]);

  const isValid = useMemo(() => {
    if (!hoveredPosition || hoveredCells.length === 0) return true;
    return !hoveredCells.some((key) => occupiedCells.has(key));
  }, [hoveredCells, occupiedCells, hoveredPosition]);

  if (!isActive) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="absolute inset-0 pointer-events-none z-[1]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          left: 0,
          top: 0,
          width: cols * gridCellWidth,
          height: rows * gridCellHeight,
        }}
      >
        {/* Visual outline for the whole hovered item */}
        {hoveredPosition && (
          <div
            className="absolute rounded-xl border-2 pointer-events-none transition-colors duration-150"
            style={{
              left:
                hoveredPosition.col * gridCellWidth +
                (hoveredSize === "icon" ? iconOffsetX : 0),
              top:
                hoveredPosition.row * gridCellHeight +
                (hoveredSize === "icon" ? iconOffsetY : 0),
              width:
                hoveredSize === "icon"
                  ? 80
                  : WIDGET_GRID_SIZE[hoveredSize].cols * gridCellWidth,
              height:
                hoveredSize === "icon"
                  ? 100
                  : WIDGET_GRID_SIZE[hoveredSize].rows * gridCellHeight,
              borderColor: isValid
                ? "rgba(34, 197, 94, 0.6)"
                : "rgba(239, 68, 68, 0.6)",
              boxShadow: `0 0 15px ${
                isValid ? "rgba(34, 197, 94, 0.2)" : "rgba(239, 68, 68, 0.2)"
              }`,
            }}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export default memo(DesktopGridOverlay);
